<template>
  <CRow class="mb-3">
    <!-- LEFT: current record -->
    <CCol lg="6">
      <CCard class="h-100">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon :name="isOrderItem ? 'cipLayers' : 'cipCoin'" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="chargeback.detail" v-if="isOrderItem">Order item details</translate>
            <translate translate-context="chargeback.detail" v-else>Handling fee details</translate>
          </div>
        </CCardHeader>
        <CCardBody>
          <div v-if="isOrderItem" class="record-block__header mb-3">
            <span class="font-weight-medium mr-2">{{ recordLabel }}</span>
            <code v-if="picacode" class="picacode-tag mr-2">
              <CIcon name="cil-qr-code" size="sm" class="mr-1"/>{{ picacode }}
            </code>
            <span v-if="record.order_item_id" class="text-muted small">#{{ record.order_item_id }}</span>
          </div>

          <div class="order-row" v-if="recordChargebackAmount > 0">
            <div class="label">
              <translate translate-context="chargeback.detail">Chargeback amount</translate>
            </div>
            <div>{{ recordChargebackAmount|currency('EUR') }}</div>
          </div>
          <div class="order-row" v-if="recordDisputeFee > 0">
            <div class="label pl-3">
              <translate translate-context="chargeback.detail">Dispute fee</translate>
            </div>
            <div>+{{ recordDisputeFee|currency('EUR') }}</div>
          </div>
          <div class="order-row order-row--border" v-if="recordTotalLoss !== 0">
            <div class="label font-weight-semi-bold">
              <translate translate-context="chargeback.detail">Record loss</translate>
            </div>
            <div class="text-danger font-weight-semi-bold">{{ recordTotalLoss|currency('EUR') }}</div>
          </div>

          <div v-if="recordStakeholderSlices.length" class="stakeholder-section mt-3">
            <div class="stakeholder-section__title">
              <translate translate-context="chargeback.detail">Per stakeholder</translate>
            </div>
            <div
              v-for="slice in recordStakeholderSlices"
              :key="slice.key"
              class="order-row"
            >
              <div class="label">
                <span class="dot mr-2" :class="`bg-${slice.color}`"/>
                {{ slice.label }}
              </div>
              <div>{{ slice.amount|currency('EUR') }}</div>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </CCol>

    <!-- RIGHT: dispute aggregates -->
    <CCol lg="6">
      <CCard class="h-100">
        <CCardHeader class="border-0 px-4 pb-1 pt-4">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipMoney" size="custom-size" :width="18" class="mr-2"/>
            <translate translate-context="chargeback.detail">Dispute totals</translate>
            <span v-if="recordsCount > 1" class="text-muted small ml-2">
              (<translate
                translate-context="chargeback.detail"
                :translate-params="{count: recordsCount}"
              >part of %{count} records</translate>)
            </span>
          </div>
        </CCardHeader>
        <CCardBody>
          <div class="order-row" v-if="disputeChargebackAmount > 0">
            <div class="label">
              <translate translate-context="chargeback.detail">Chargeback amount</translate>
            </div>
            <div>{{ disputeChargebackAmount|currency('EUR') }}</div>
          </div>
          <div class="order-row" v-if="disputeDisputeFee > 0">
            <div class="label pl-3">
              <translate translate-context="chargeback.detail">Dispute fee</translate>
              <span class="text-muted small ml-2">
                (<translate translate-context="chargeback.detail">Stripe penalty</translate>)
              </span>
            </div>
            <div>+{{ disputeDisputeFee|currency('EUR') }}</div>
          </div>
          <div class="order-row order-row--border" v-if="disputeTotalLoss !== 0">
            <div class="label font-weight-semi-bold">
              <translate translate-context="chargeback.detail">Total loss</translate>
            </div>
            <div class="text-danger font-weight-semi-bold">{{ disputeTotalLoss|currency('EUR') }}</div>
          </div>

          <div v-if="disputeStakeholderSlices.length" class="stakeholder-section mt-3">
            <div class="stakeholder-section__title">
              <translate translate-context="chargeback.detail">Per stakeholder</translate>
            </div>
            <div
              v-for="slice in disputeStakeholderSlices"
              :key="slice.key"
              class="order-row"
            >
              <div class="label">
                <span class="dot mr-2" :class="`bg-${slice.color}`"/>
                {{ slice.label }}
              </div>
              <div>{{ slice.amount|currency('EUR') }}</div>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import {getStakeholderColor} from '../../constants'

export default {
  name: 'ChargebackAmountsWaterfall',
  props: {
    record: {
      type: Object,
      required: true
    }
  },
  computed: {
    dispute() {
      return this.record.dispute || {}
    },
    recordData() {
      return this.record.record || {}
    },
    isOrderItem() {
      const st = this.record.source_type || (this.record.order_item_id ? 'order_item' : 'handling_fee')
      return st === 'order_item'
    },
    recordLabel() {
      return this.record.pica_service_name || `Order item #${this.record.order_item_id}`
    },
    picacode() {
      return this.record.picacode || this.record.context?.picacode || null
    },
    recordChargebackAmount() {
      return parseFloat(this.recordData.chargeback_amount) || 0
    },
    recordDisputeFee() {
      return parseFloat(this.recordData.dispute_fee) || 0
    },
    recordTotalLoss() {
      const t = parseFloat(this.recordData.total_loss)
      if (!isNaN(t)) return t
      return this.recordChargebackAmount + this.recordDisputeFee
    },
    recordStakeholderSlices() {
      return this.buildSlices({
        photographer: this.recordData.photographer_loss,
        pica: this.recordData.pica_loss,
        organizer: this.recordData.organizer_loss,
      })
    },
    recordsCount() {
      return parseInt(this.dispute.records_count) || 0
    },
    disputeChargebackAmount() {
      return parseFloat(this.dispute.total_chargeback_amount) || 0
    },
    disputeDisputeFee() {
      return parseFloat(this.dispute.total_dispute_fee) || 0
    },
    disputeTotalLoss() {
      const t = parseFloat(this.dispute.total_loss)
      if (!isNaN(t)) return t
      return this.disputeChargebackAmount + this.disputeDisputeFee
    },
    disputeStakeholderSlices() {
      return this.buildSlices({
        photographer: this.dispute.photographer_loss,
        pica: this.dispute.pica_loss,
        organizer: this.dispute.organizer_loss,
      })
    }
  },
  methods: {
    buildSlices({photographer, pica, organizer}) {
      const slices = []
      const push = (key, type, label, rawAmount) => {
        const amount = parseFloat(rawAmount) || 0
        if (amount <= 0) return
        slices.push({
          key,
          label,
          amount,
          color: getStakeholderColor(type)
        })
      }
      push('photographer', 'photographer', this.$pgettext('chargeback.detail', 'Photographer'), photographer)
      push('pica', 'pica', 'Pica', pica)
      push('organizer', 'organizer', this.$pgettext('chargeback.detail', 'Organizer'), organizer)
      return slices
    }
  }
}
</script>

<style scoped>
.picacode-tag {
  background: #f4f6f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  vertical-align: middle;
}

.stakeholder-section__title {
  font-size: 0.75rem;
  color: #6c7285;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 8px;
  padding-top: 8px;
  border-top: 1px solid #eef1f5;
}
</style>
